/**
 * Campos de ticket que acepta `PATCH {api}/update/field/tickets/{recordId}`. `status` NO
 * está acá: tiene su propio endpoint `{api}/tickets/{recordId}/status`.
 */
export enum TicketField {
  ASSIGNED_ADVISOR = 'assignedAdvisor',
  DESCRIPTION = 'description',
  SOLUTION = 'solution',
  USER_RESPONSE = 'userResponse',
  PRIORITY = 'priority',
  CLOSURE_TYPE = 'closureType',
  INCIDENT_TYPES = 'incidentTypes',
  LAST_UPDATE_INCIDENT_TYPE_DATE = 'lastUpdateIncidentTypeDate',
  LAST_STATE_UPDATE_DATE = 'lastStateUpdateDate',
  LAST_RECORD_UPDATE_DATE = 'lastRecordUpdateDate',
  METADATA = 'metadata',
  REPORTED_BY = 'reportedBy',
}

const TICKET_FIELD_VALUES = new Set<string>(Object.values(TicketField));

/**
 * Nombre de campo del front (en español) → campo del contrato migrado. Los 4 tipos de
 * ticket comparten este diccionario porque comparten endpoint.
 */
const TICKET_FIELD_BY_NAME: Record<string, TicketField> = {
  incidencia: TicketField.DESCRIPTION,
  incidence: TicketField.DESCRIPTION,
  descripcion: TicketField.DESCRIPTION,
  solucion: TicketField.SOLUTION,
  resolucion: TicketField.SOLUTION,
  asesorAsignado: TicketField.ASSIGNED_ADVISOR,
  asignado: TicketField.ASSIGNED_ADVISOR,
  respuestaUsuario: TicketField.USER_RESPONSE,
  responseUser: TicketField.USER_RESPONSE,
  tipoCierre: TicketField.CLOSURE_TYPE,
  tipoIncidencia: TicketField.INCIDENT_TYPES,
  reportador: TicketField.REPORTED_BY,
};

/**
 * Resuelve el `fieldName` del contrato migrado, o `null` si el campo no
 * pertenece a `ticketUpdatableFields`. No inventa nombres.
 */
export function toTicketField(field: string): TicketField | null {
  if (TICKET_FIELD_VALUES.has(field)) return field as TicketField;

  return TICKET_FIELD_BY_NAME[field] ?? null;
}

/**
 * `incidentTypes` viaja siempre como arreglo, aunque la vista edite un solo
 * valor. El resto de campos pasa tal cual (el back acepta string, objeto o null).
 */
export function toTicketFieldValue(field: TicketField, value: unknown): unknown {
  if (field !== TicketField.INCIDENT_TYPES) return value;

  if (Array.isArray(value)) return value;
  return value == null || value === '' ? [] : [value];
}

/**
 * Campos del front que no son columna del ticket sino una clave dentro de `metadata`. La
 * clave no es uniforme: las del ETL van en snake_case y las del alta en camelCase.
 */
const TICKET_METADATA_KEY_BY_FIELD: Record<string, string> = {
  // BCP (`sc_temporal.ticket_incidents`)
  /** Misma clave que escribe el alta (`register()`): nunca `monto_devolucion`. */
  montoDevolucion: 'refundAmount',
  fechaDevolucion: 'fecha_devolucion',
  /** El alta la escribe así (`register()`), no como `asesor_crece`. */
  asesorCrece: 'advisorCrece',
  // Desde cliente (`sc_temporal.ticket_client`)
  dependencia: 'dependencia',
};

/** Clave dentro de `metadata`, o `null` si el campo no vive ahí. */
export function toTicketMetadataKey(field: string): string | null {
  return TICKET_METADATA_KEY_BY_FIELD[field] ?? null;
}
